import React, { useEffect, useState } from "react";
import { Snowflake, Zap, Save, Wallet, Sparkles, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { fmt } from "@/lib/constants";
import { toast } from "sonner";
import HelpTip from "@/components/HelpTip";

const STRAT_LABEL = { snowball: "Snowball", avalanche: "Avalanche", cfi: "Cash Flow Index" };

export default function PayoffPlanCard({ plan, comparison, onSaved }) {
  const [strategy, setStrategy] = useState(plan?.strategy || "avalanche");
  const [snowball, setSnowball] = useState(plan?.snowball_amount ?? 0);
  const [saving, setSaving] = useState(false);
  const [rec, setRec] = useState(null);

  useEffect(() => { if (plan) { setStrategy(plan.strategy); setSnowball(plan.snowball_amount); } }, [plan]);
  useEffect(() => { api.get("/debts/strategy-recommendation").then(r => setRec(r.data)).catch(() => {}); }, [plan]);
  const dirty = plan && (plan.strategy !== strategy || Number(plan.snowball_amount) !== Number(snowball || 0));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/payoff-plan", { strategy, snowball_amount: parseFloat(snowball) || 0 });
      toast.success("Payoff plan saved");
      onSaved?.();
    } catch { toast.error("Could not save plan"); }
    finally { setSaving(false); }
  };

  const applyRec = async () => {
    setStrategy(rec.recommended);
    setSaving(true);
    try {
      await api.put("/payoff-plan", { strategy: rec.recommended, snowball_amount: parseFloat(snowball) || 0 });
      toast.success(`Switched to ${STRAT_LABEL[rec.recommended]}`);
      onSaved?.();
    } catch { toast.error("Could not apply suggestion"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-5 rounded-2xl border border-border bg-card" data-testid="payoff-plan-card">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-2">My payoff plan <HelpTip k="snowball" /> <HelpTip k="avalanche" /></div>

      {rec && !rec.trivial && (
        <div className="mt-3 p-3 rounded-xl border border-teal-500/30 bg-teal-500/5" data-testid="strategy-recommendation">
          <div className="flex items-start gap-2.5">
            <span className="h-7 w-7 rounded-lg bg-teal-500/15 text-teal-500 flex items-center justify-center shrink-0"><Sparkles className="h-4 w-4" /></span>
            <div className="min-w-0">
              <div className="text-xs font-semibold flex items-center gap-1.5 flex-wrap">
                OWEverit suggests <span className="text-teal-500">{STRAT_LABEL[rec.recommended]}</span>
                {rec.recommended === strategy && <span className="text-[10px] font-normal text-emerald-500" data-testid="rec-on-it">· you're on it ✓</span>}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">{rec.reason}</p>
              {rec.recommended !== strategy && (
                <button onClick={applyRec} disabled={saving} data-testid="plan-apply-recommendation"
                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-teal-600 dark:text-teal-400 hover:gap-1.5 transition-all disabled:opacity-50">
                  Apply {STRAT_LABEL[rec.recommended]} <ArrowRight className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-center gap-6 md:gap-10">
        {/* Strategy */}
        <div className="flex flex-col items-center text-center">
          <div className="inline-flex flex-wrap justify-center bg-accent p-1 rounded-xl border border-border">
            <button onClick={() => setStrategy("snowball")} data-testid="plan-strategy-snowball"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${strategy === "snowball" ? "bg-cyan-500 text-white shadow" : "text-muted-foreground hover:text-foreground"}`}>
              <Snowflake className="h-4 w-4" /> Snowball
            </button>
            <button onClick={() => setStrategy("avalanche")} data-testid="plan-strategy-avalanche"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${strategy === "avalanche" ? "bg-violet-600 text-white shadow" : "text-muted-foreground hover:text-foreground"}`}>
              <Zap className="h-4 w-4" /> Avalanche
            </button>
            <button onClick={() => setStrategy("cfi")} data-testid="plan-strategy-cfi"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${strategy === "cfi" ? "bg-emerald-600 text-white shadow" : "text-muted-foreground hover:text-foreground"}`}>
              <Wallet className="h-4 w-4" /> Cash Flow Index
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-3 leading-relaxed max-w-[280px]">
            {strategy === "snowball"
              ? "Target the smallest balance first — quick wins build momentum."
              : strategy === "cfi"
              ? "Target the debt that frees the most monthly cash flow first (lowest balance ÷ minimum payment)."
              : "Target the highest APR first — the cheapest path out of debt."}
          </p>
        </div>

        {/* Snowball amount + save */}
        <div className="md:border-l md:border-border md:pl-10 flex flex-col items-center text-center">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground inline-flex items-center gap-1">Monthly snowball (extra beyond minimums) <HelpTip k="snowball_amount" /></span>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-muted-foreground text-sm">$</span>
            <input type="number" min={0} step={5} value={snowball} onChange={e => setSnowball(e.target.value)} data-testid="plan-snowball-input"
              className="w-32 px-3 py-2 rounded-lg bg-background border border-border text-sm font-mono text-center focus:outline-none focus:ring-2 focus:ring-indigo-500/40" />
            <span className="text-xs text-muted-foreground">/ mo</span>
          </div>
          <span className="text-[11px] text-muted-foreground mt-1.5 max-w-[240px]">Freed minimums roll into your target automatically.</span>
          <button onClick={save} disabled={saving || !dirty} data-testid="plan-save-btn"
            className="mt-4 flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-500 text-white text-sm font-semibold hover:bg-indigo-600 transition disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : dirty ? "Save plan" : `Plan saved · ${fmt(plan?.snowball_amount)}/mo`}
          </button>
        </div>
      </div>
    </div>
  );
}
